import { type ThemeUIStyleObject } from 'theme-ui';
import { keyframes } from '@emotion/react';
import colors from '@revdotcom/vaporwave-color';

const showWithDelay = keyframes`
    from, 99% { visibility: hidden; }
    to { visibility: visible; }
`;

const styles: Record<string, ThemeUIStyleObject> = {
    cardLogin: {
        position: 'relative',
        px: ['2.5rem', '6.25rem'],
        py: '2.5rem',
        width: ['auto', '32.75rem'],
        minWidth: '20rem',
        mx: 'auto',
        mt: [0, '6rem'],
        variant: 'cards.identity.login',
    },
    cardForgotPassword: {
        position: 'relative',
        px: ['2.5rem', '6.25rem'],
        py: '2.5rem',
        width: ['auto', '32.75rem'],
        minWidth: '20rem',
        mx: 'auto',
        mt: [0, '6rem'],
        variant: 'cards.identity.forgotPassword',
    },
    cardVerifyPassword: {
        position: 'relative',
        px: ['2.5rem', '6.25rem'],
        py: '2.5rem',
        width: ['auto', '32.75rem'],
        minWidth: '20rem',
        mx: 'auto',
        mt: [0, '6rem'],
        variant: 'cards.identity.verifyPassword',
    },
    cardContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        variant: 'cards.identity.container',
    },
    cardLogo: {
        width: '140px',
        pb: 4,
        mx: 'auto',
        variant: 'cards.identity.logo',
    },
    cardTitle: {
        display: 'block',
        m: 0,
        color: 'brand-primary',
        fontSize: '1.625rem',
        fontWeight: '900',
        lineHeight: '1.05',
        variant: 'cards.identity.title',
    },
    cardForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2.5rem',
        variant: 'cards.identity.form',
    },
    cardError: {
        position: 'absolute',
        top: '100%',
        left: 0,
        display: 'inline-block',
        mt: 2,
        py: 1,
        px: '0.75rem',
        fontSize: 0,
        background: colors.light.pink[60],
        color: 'white',
        borderRadius: 2,
        variant: 'forms.identity.login.error',
    },
    cardShowPassword: {
        position: 'absolute',
        top: 0,
        right: 0,
        p: 0,
        color: 'gray',
        fontSize: 0,
        lineHeight: 'third',
        letterSpacing: 'medium',
        variant: 'forms.identity.login.showPassword',
    },
    cardRememberPassword: {
        display: 'block',
        fontSize: 2,
        color: 'gray-60',
        variant: 'forms.identity.login.rememberPassword',
    },
    cardForgotPasswordLink: {
        p: 0,
        display: 'block',
        fontSize: 1,
        color: 'primary-60',
        textAlign: 'left',
        variant: 'forms.identity.login.passwordReset',
    },
    cardSignupLink: {
        p: 0,
        display: 'block',
        fontSize: 1,
        color: 'primary-60',
        variant: 'forms.identity.login.signupLink',
    },
    cardUnderLinks: {
        position: 'absolute',
        right: ['50%', 0, 0],
        translate: ['50%', 'none', 'none'],
        bottom: '-2rem',
        display: 'flex',
        flexDirection: 'row',
        gap: '0.75rem',
    },
    cardUnderLink: {
        color: 'brand-primary',
        fontSize: 0,
        variant: 'forms.identity.login.underLink',
    },
    cardSendMfaCode: {
        p: 0,
        display: 'block',
        fontSize: 1,
        color: 'primary-60',
        textAlign: 'left',
        variant: 'forms.identity.login.sendMfaCode',
    },
    mfaDialog: {
        top: '22.5rem',
        width: '35rem',
    },
    mfaDialogText: {
        display: 'block',
        pb: 5,
        fontSize: 1,
        color: 'gray-60',
    },
    mfaDialogButton: {
        display: 'block',
        width: ['100%', '20rem'],
        mx: 'auto',
        fontSize: 1,
    },
    verifyPasswordPromptText: {
        display: 'block',
        pb: 2,
        fontSize: 1,
        color: 'gray-60',
    },
    landingButton: {
        display: 'block',
        mx: 'auto',
        mt: '5rem',
        width: '20rem',
        textAlign: 'center',

        // make the button to appear after a delay, to let
        // the meta-refresh kick-in without the flickering the button
        visibility: 'hidden',
        animation: `${showWithDelay} 4s linear`,
        animationFillMode: 'forwards',
    },
    cardResetPasswordButton: {
        fontSize: 1,
        display: 'block',
        variant: 'forms.identity.login.resetPassword',
    },
    cardText: {
        display: 'block',
        fontSize: 1,
        color: 'gray-60',
    },
    socialLoginButtons: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        variant: 'cards.identity.login.socialLoginButtons',
    },
    // banner styling
    cardBanner: {
        display: 'flex',
        width: '100%',
        py: 2,
        px: 3,
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '4px',
        borderRadius: '8px',
        border: '1px solid #CDDBF3',
        backgroundColor: 'white',
        boxShadow: '4px 4px 0px 0px #CDDBF3',
        variant: 'cards.identity.login.banner',
        // disable default hover effect
        '&:hover': {
            boxShadow: '4px 4px 0px 0px #CDDBF3',
        },
    },
    cardBannerText: {
        color: 'black',
        fontSize: 1,
        textAlign: 'right',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        mx: 0,
        my: 0,
        variant: 'cards.identity.login.bannerText',
    },
    cardBannerLink: {
        color: 'primary',
        textAlign: 'center',
        display: 'block',
        mt: 2,
        fontSize: 0,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        textDecoration: 'none',
        variant: 'cards.identity.login.bannerLink',
    },
};

export default styles;
